import React, { useState, useEffect } from 'react';
import { Grid, styled } from '@mui/material';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { send_request } from '../../../utils/Request';
import { formatRelativeTime } from '../agentSessionsPanelUtils';
import { DashboardCard, CardTitle, StyledSkeleton } from './CommonComponents';

const WorkflowRow = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '6px 8px',
  borderRadius: 6,
  cursor: 'pointer',
  transition: 'background 120ms ease',
  '&:hover': {
    background: theme.palette.action.hover,
  },
}));

const ViewAllLink = styled('button')(({ theme }) => ({
  background: 'none',
  border: 'none',
  color: theme.palette.text.secondary,
  fontSize: 12,
  fontWeight: 500,
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  padding: '4px 0',
  transition: 'color 120ms ease',
  '&:hover': {
    color: theme.palette.text.primary,
  },
}));

export default function RecentWorkflowsCompactCard() {
  const [workflows, setWorkflows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchWorkflows = async () => {
      try {
        const json = await send_request(
          'project-service/project/getRecentlyUpdatedProjects/3',
          null,
          null,
          'GET'
        );
        if (json?.data) {
          setWorkflows(json.data.filter(w => w.status !== 'DELETED'));
        }
      } catch (e) {
        console.error('Failed to load recent workflows:', e);
      } finally {
        setLoading(false);
      }
    };
    fetchWorkflows();
  }, []);

  if (loading) {
    return (
      <DashboardCard container rowGap="8px">
        <Grid item xs={12}>
          <CardTitle>Recent Workflows</CardTitle>
        </Grid>
        <Grid item xs={12}>
          {[0, 1, 2].map(i => (
            <StyledSkeleton key={i} variant="rectangular" height="32px" margin="0 0 4px 0" />
          ))}
        </Grid>
      </DashboardCard>
    );
  }

  if (workflows.length === 0) {
    return (
      <DashboardCard container rowGap="8px">
        <Grid item xs={12}>
          <CardTitle>Recent Workflows</CardTitle>
        </Grid>
        <Grid item xs={12}>
          <div style={{
            color: 'var(--color-text-secondary, #71717a)',
            fontSize: 13,
            padding: '12px 0',
            textAlign: 'center',
          }}>
            No workflows yet.
          </div>
        </Grid>
      </DashboardCard>
    );
  }

  return (
    <DashboardCard container rowGap="4px">
      <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <CardTitle>Recent Workflows</CardTitle>
        <ViewAllLink onClick={() => window.open('/project', '_blank')}>
          Library <ArrowForwardIcon sx={{ fontSize: 12 }} />
        </ViewAllLink>
      </Grid>
      <Grid item xs={12}>
        {workflows.map(wf => (
          <WorkflowRow
            key={wf.projectId}
            onClick={() => window.open(`/project/canvas/${wf.projectId}`, '_blank')}
          >
            <DescriptionOutlinedIcon sx={{ fontSize: 16, color: 'text.disabled', flexShrink: 0 }} />
            <span style={{
              fontSize: 13,
              fontWeight: 500,
              flex: 1,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}>
              {wf.name || 'Untitled'}
            </span>
            <span style={{
              fontSize: 11,
              color: 'var(--color-text-secondary, #a1a1aa)',
              flexShrink: 0,
            }}>
              {formatRelativeTime(wf.lastModified || wf.lastPublished)}
            </span>
          </WorkflowRow>
        ))}
      </Grid>
    </DashboardCard>
  );
}
